import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface LogEntry {
  id: number;
  userEmail: string;
  category: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  detail: string | null;
  createdAt: string;
}

interface LogsResponse {
  logs: LogEntry[];
  total: number;
  retentionDays: number;
  page: number;
  pageSize: number;
}

const CATEGORIES = [
  "Auth",
  "Users",
  "Workflows",
  "Rules",
  "Bots",
  "Deployments",
  "Reports",
  "KB",
  "Settings",
  "Teams"
];

const CATEGORY_COLORS: Record<string, string> = {
  Auth: "bg-purple-100 text-purple-700",
  Users: "bg-blue-100 text-blue-700",
  Workflows: "bg-cyan-100 text-cyan-700",
  Rules: "bg-orange-100 text-orange-700",
  Bots: "bg-indigo-100 text-indigo-700",
  Deployments: "bg-teal-100 text-teal-700",
  Reports: "bg-green-100 text-green-700",
  KB: "bg-yellow-100 text-yellow-700",
  Settings: "bg-slate-100 text-slate-700",
  Teams: "bg-pink-100 text-pink-700"
};

const ACTION_ICONS: Record<string, string> = {
  Created: "➕",
  Deleted: "🗑",
  Updated: "✏️",
  Published: "✅",
  Unpublished: "⬇️",
  Enabled: "🟢",
  Disabled: "🔴",
  Generated: "📊",
  Emailed: "📧",
  LoggedIn: "🔑",
  Invited: "📨",
  Removed: "❌"
};

function getActionIcon(action: string): string {
  for (const [key, icon] of Object.entries(ACTION_ICONS)) {
    if (action.includes(key)) return icon;
  }
  return "•";
}

function formatAction(action: string): string {
  // "WorkflowDeleted" → "Workflow Deleted"
  return action.replace(/([A-Z])/g, " $1").trim();
}

export default function ActivityLog() {
  const api = useApi();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [expandedDetail, setExpandedDetail] = useState<number | null>(null);

  const pageSize = 50;
  const totalPages = Math.ceil(total / pageSize);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
      });
      if (category) params.set("category", category);
      if (search) params.set("search", search);

      const resp = await api.admin.get(`ActivityLog?${params}`);
      const data: LogsResponse = await resp.json();
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setTotal(data.total ?? 0);
      setRetentionDays(data.retentionDays ?? 30);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, page, category, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  function handleCategoryChange(cat: string) {
    setCategory(cat);
    setPage(1);
  }

  function formatDetail(detail: string | null): string | null {
    if (!detail) return null;
    try {
      const obj = JSON.parse(detail);
      return JSON.stringify(obj, null, 2);
    } catch {
      return detail;
    }
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-slate-500">
            Showing activity from the last {retentionDays} days &nbsp;·&nbsp;{" "}
            {total.toLocaleString()} events total
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Search user, action, entity…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button
            type="submit"
            className="bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              className="text-slate-500 text-sm underline"
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Category filter pills */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => handleCategoryChange("")}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            category === ""
              ? "bg-slate-800 text-white border-slate-800"
              : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
          }`}
        >
          All
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => handleCategoryChange(cat)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              category === cat
                ? "bg-slate-800 text-white border-slate-800"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Log table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left font-medium">When</th>
              <th className="px-4 py-3 text-left font-medium">User</th>
              <th className="px-4 py-3 text-left font-medium">Category</th>
              <th className="px-4 py-3 text-left font-medium">Action</th>
              <th className="px-4 py-3 text-left font-medium">Entity</th>
              <th className="px-4 py-3 text-left font-medium w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  No activity found
                  {category || search
                    ? " matching your filters"
                    : " yet — actions appear here as users make changes"}
                  .
                </td>
              </tr>
            )}
            {!loading &&
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {log.createdAt}
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate">
                      {log.userEmail || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                          CATEGORY_COLORS[log.category] ??
                          "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {log.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-800 font-medium">
                      <span className="mr-1.5">
                        {getActionIcon(log.action)}
                      </span>
                      {formatAction(log.action)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">
                      {log.entityName ? (
                        <span title={log.entityName}>{log.entityName}</span>
                      ) : log.entityId ? (
                        <span className="text-slate-400">
                          {log.entityType} #{log.entityId}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {log.detail && (
                        <button
                          onClick={() =>
                            setExpandedDetail(
                              expandedDetail === log.id ? null : log.id
                            )
                          }
                          className="text-blue-500 hover:text-blue-700 text-xs"
                          aria-label="Toggle detail"
                        >
                          {expandedDetail === log.id ? "▲" : "▼"}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedDetail === log.id && log.detail && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 pb-3 pt-0">
                        <pre className="text-xs text-slate-600 bg-slate-100 rounded p-3 overflow-x-auto max-h-40 font-mono">
                          {formatDetail(log.detail)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
          <span>
            Page {page} of {totalPages} &nbsp;({total.toLocaleString()} total)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400">
        Logs are retained for {retentionDays} days based on your subscription
        tier. Contact Velaro support to discuss extended retention.
      </p>
    </div>
  );
}
